import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { CarItemCard } from '@components/Home/CarItemCard';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { Trans } from '@lingui/react/macro';
import { URLKeys, noddiAsync } from 'noddi-async';
import { NoddiButton, NoddiLink } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useState } from 'react';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';

const DEFAULT_NUMBER_OF_CARS_TO_DISPLAY = 3;
const EXPAND_THRESHOLD = 3; // If more than this many extra cars, navigate to dedicated page

type CarsProps = {
  userGroupId: number;
};

export const Cars = ({ userGroupId }: CarsProps) => {
  const [showAllCars, setShowAllCars] = useState(false);
  const { currentBrand } = useCurrentBrand();

  const {
    isPending: isCarsPending,
    data: paginatedFleetViewCars,
    error: carsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetViewCars,
    input: {
      userGroupIds: [userGroupId],
      pageSize: showAllCars ? 1000 : DEFAULT_NUMBER_OF_CARS_TO_DISPLAY,
      pageIndex: 0,
      brandDomains: [currentBrand]
    }
  });

  if (carsError) {
    return <ApiErrorPageWithTrans error={carsError} />;
  }

  if (isCarsPending) {
    return <NoddiLoadingScreenWithTrans />;
  }
  const cars = paginatedFleetViewCars?.results;
  const totalCars = paginatedFleetViewCars?.count;
  const isAreMoreCars = totalCars > DEFAULT_NUMBER_OF_CARS_TO_DISPLAY;
  const remainingCars = totalCars - DEFAULT_NUMBER_OF_CARS_TO_DISPLAY;
  const shouldNavigateToPage = remainingCars > EXPAND_THRESHOLD;

  if (!cars.length) {
    return null;
  }

  return (
    <div className='flex flex-col gap-2 md:gap-4'>
      <h2 className='text-xl font-bold text-primary md:text-3xl'>
        <Trans>Cars</Trans>
      </h2>
      <div className='flex flex-col gap-3'>
        {cars.map((car) => (
          <CarItemCard key={car.id} car={car} />
        ))}
      </div>
      {isAreMoreCars && (
        <div className='mt-1 flex w-full justify-between px-5'>
          {!showAllCars && (
            <p className='text-xl font-semibold'>
              {`+ ${remainingCars}`} <Trans>more</Trans>
            </p>
          )}
          {shouldNavigateToPage ? (
            <NoddiLink variant='outline' size='small' className='ml-auto' to={customerRoutes.myCars.getPath()}>
              <Trans>Show all</Trans>
            </NoddiLink>
          ) : (
            <NoddiButton
              onClick={() => setShowAllCars((prev) => !prev)}
              variant='outline'
              size='small'
              className='ml-auto'
            >
              {showAllCars ? <Trans>Show less</Trans> : <Trans>Show all</Trans>}
            </NoddiButton>
          )}
        </div>
      )}
    </div>
  );
};
